import { ApiEndpoints } from "@shared/api/api-endpoints";
import { cxApiClient } from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { Note } from "../types/consumerRequest";
import { CreateNoteInput } from "../types/notesTabProps";

// Note Types
export interface NoteFilters {
  remoteUtilityId?: number;
  requestId?: number;
  noteType?: string;
  source?: string;
  createdBy?: string;
  page?: number;
  limit?: number;
  searchData?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

export interface NoteListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  result: Note[];
}

export interface UpdateNotePayload {
  text: string;
  internal?: boolean;
  noteType?: string;
}

export interface NoteTypeChoice {
  key: string;
  value: string;
}

// Notes API Functions
export const notesApi = {
  // Get Notes List
  getNotes: async (filters: NoteFilters): Promise<NoteListResponse> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-request",
      ["notes"],
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", getRemoteUtilityId());
        if (filters.requestId)
          query.push("request_id", filters.requestId.toString());
        if (filters.noteType) query.push("note_type", filters.noteType);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.source) query.push("source", filters.source);
        if (filters.dateRange?.start)
          query.push("start_date", filters.dateRange.start);
        if (filters.dateRange?.end)
          query.push("end_date", filters.dateRange.end);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<NoteListResponse>(response.data);
  },

  // Get Note Detail
  getNoteDetail: async (noteId: string): Promise<{ result: Note }> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "notes", [
      noteId,
    ]);

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: Note }>(response.data);
  },

  // Create Note
  createNote: async (payload: CreateNoteInput): Promise<{ result: Note }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-request",
      ["notes"],
    );
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<{ result: Note }>(response.data);
  },
  // Get Note Types
  getNoteTypes: async (): Promise<{ result: NoteTypeChoice[] }> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "choices", [
      "note_type",
    ]);
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: NoteTypeChoice[] }>(response.data);
  },

  // Get Notes Summary
  getNotesSummary: async (params: {
    remoteUtilityId: number;
    requestId?: string;
    consumerId?: string;
  }): Promise<{
    result: {
      total: number;
      internal: number;
      external: number;
      byType: Array<{
        type: string;
        count: number;
      }>;
    };
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "notes/summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        if (params.requestId) query.push("request_id", params.requestId);
        if (params.consumerId) query.push("consumer_id", params.consumerId);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{
      result: {
        total: number;
        internal: number;
        external: number;
        byType: Array<{
          type: string;
          count: number;
        }>;
      };
    }>(response.data);
  },
};
